import { useEffect, useRef } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from './icons'

interface FloatingPanelProps extends PropsWithChildren {
  open: boolean
  title: string
  description?: string
  position?: { x: number; y: number } | null
  width?: number
  variant?: 'default' | 'legacy'
  anchorX?: 'center' | 'left'
  placement?: 'anchored' | 'top-center'
  footer?: ReactNode
  ignoreOutsideSelector?: string
  headerClassName?: string
  bodyClassName?: string
  onClose: () => void
}

export function FloatingPanel({
  open,
  title,
  description,
  position,
  width = 360,
  variant = 'default',
  anchorX = 'center',
  placement = 'anchored',
  footer,
  ignoreOutsideSelector,
  headerClassName,
  bodyClassName,
  onClose,
  children,
}: FloatingPanelProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-overlay-root="true"]')) {
        return
      }
      if (
        ignoreOutsideSelector &&
        event.target instanceof Element &&
        event.target.closest(ignoreOutsideSelector)
      ) {
        return
      }
      if (!rootRef.current?.contains(event.target as Node)) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handlePointerDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handlePointerDown, true)
    }
  }, [ignoreOutsideSelector, onClose, open])

  if (!open) return null

  const left = placement === 'top-center'
    ? Math.max(12, Math.min(window.innerWidth / 2 - width / 2, window.innerWidth - width - 12))
    : Math.max(
      12,
      Math.min(
        anchorX === 'left'
          ? (position?.x ?? window.innerWidth / 2)
          : (position?.x ?? window.innerWidth / 2) - width / 2,
        window.innerWidth - width - 12,
      ),
    )
  const top = placement === 'top-center'
    ? 56
    : Math.max(72, Math.min((position?.y ?? window.innerHeight / 2) + 12, window.innerHeight - 180))
  const isLegacy = variant === 'legacy'

  return (
    <div
      ref={rootRef}
      className={`fixed z-[1260] overflow-hidden border shadow-2xl ${isLegacy ? 'rounded-[10px]' : 'rounded-2xl'}`}
      style={{
        left,
        top,
        width,
        maxHeight: 'calc(100vh - 88px)',
        background: isLegacy ? 'var(--bg-popup)' : 'var(--bg-modal)',
        borderColor: 'var(--border)',
        color: 'var(--text)',
      }}
    >
      <div
        className={`flex justify-between gap-3 border-b ${isLegacy ? 'items-center px-[10px] py-[10px]' : 'items-start px-4 py-3'} ${headerClassName ?? ''}`.trim()}
        style={{ borderColor: 'var(--border)' }}
      >
        <div>
          <h3 className={isLegacy ? 'text-[11px] font-semibold leading-none' : 'text-sm font-semibold'}>{title}</h3>
          {description ? (
            <p className={`${isLegacy ? 'mt-1 text-[10px]' : 'mt-1 text-[11px]'}`} style={{ color: 'var(--text-dim)' }}>
              {description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          title={t('common.close')}
          className={`inline-flex items-center justify-center text-sm transition-colors ${isLegacy ? 'h-5 w-5' : 'h-6 w-6'}`}
          style={{ color: 'var(--text-muted)' }}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={`max-h-[calc(100vh-220px)] overflow-auto ${isLegacy ? 'px-[10px] py-[10px]' : 'px-4 py-3 text-sm'} ${bodyClassName ?? ''}`.trim()}>{children}</div>

      {footer ? (
        <div
          className={`flex items-center justify-end gap-2 border-t ${isLegacy ? 'px-[10px] py-[10px]' : 'px-4 py-3'}`}
          style={{ borderColor: 'var(--border)' }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  )
}
